import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  MenuItem,
  Modal,
  TextField,
} from "@material-ui/core";
import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";

import CloseIcon from "@material-ui/icons/Close";

import { AddIcon } from "../../../../components/icons/themes/windows10";

import useOnClickOutside from "../../../../hooks/on-click-outside";
import { useOnPressEnter } from "../../../../hooks/on-key-press";
import { theme } from "../../../../theme";
import { useModalStyles } from "../../modals/modalStyles";

export type EditTagsModalProps = {
  numEvents: number;
  onClose: () => void;
  onSave: (tags: Record<string, true>) => void;
  prevAllTags: Record<string, true>;
  prevSetTags: Record<string, true>;
};

export const EditTagsModal: React.FC<EditTagsModalProps> = ({
  prevAllTags,
  prevSetTags,
  onClose,
  onSave,
  numEvents,
}) => {
  const classes = useModalStyles();

  const [allTags, setAllTags] = useState<Record<string, true>>(prevAllTags);
  const [activeTags, setActiveTags] = useState<Record<string, true>>(
    prevSetTags
  );
  const [newTagName, setNewTagName] = useState("");
  const [addingNewTag, setAddingNewTag] = useState(false);
  const addingNewTagRef = useRef<HTMLInputElement | null>(null);

  const focusAddNewField = () => {
    if (addingNewTag && addingNewTagRef && addingNewTagRef.current) {
      addingNewTagRef.current.focus();
      addingNewTagRef.current.select();
    }
  };

  const maybeCancelAddingNewTag = () => {
    if (!newTagName) {
      setAddingNewTag(false);
    } else {
      addNewTag();
    }
  };

  const addNewTag = () => {
    setAllTags(_.extend({}, allTags, { [newTagName]: true }));
    setActiveTags(_.extend({}, activeTags, { [newTagName]: true }));
    setAddingNewTag(false);
  };

  useEffect(focusAddNewField, [addingNewTag, addingNewTagRef]);
  useOnClickOutside([addingNewTagRef], maybeCancelAddingNewTag);
  useOnPressEnter([addingNewTagRef], () => {
    if (newTagName) {
      addNewTag();
    }
  });

  function handleSave() {
    onSave(activeTags);
  }

  const tagRows: string[] = _(allTags)
    .keys()
    .orderBy((tag) => _.lowerCase(tag))
    .value();

  return (
    <Modal open={true} onClose={onClose} title="Edit Tags">
      <Card className={classes.root}>
        <CardHeader
          title={`Edit Tags for ${
            numEvents === 1 ? "1 Event" : `${numEvents} Events`
          }`}
          classes={{ action: classes.closeIconAction }}
          action={<CloseIcon onClick={onClose} />}
        />

        <Divider />

        <CardContent>
          {tagRows.map((tag) => (
            <MenuItem
              key={`tag-row-${tag}`}
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <span className="truncate">{tag}</span>
              <Checkbox
                checked={tag in activeTags}
                onChange={() => {
                  const newActiveTags =
                    tag in activeTags
                      ? _.omit(activeTags, tag)
                      : _.extend({}, activeTags, { [tag]: true });

                  setActiveTags(newActiveTags);
                }}
                value={tag in activeTags}
              />
            </MenuItem>
          ))}

          {addingNewTag && (
            <TextField
              autoFocus
              fullWidth
              placeholder="New Tag Name"
              inputRef={addingNewTagRef}
              value={newTagName}
              variant="outlined"
              size="small"
              onChange={(e) => setNewTagName(e.target.value)}
              onBlur={maybeCancelAddingNewTag}
            />
          )}

          {!addingNewTag && (
            <div style={{ textAlign: "center" }}>
              <Button
                color="primary"
                onClick={() => setAddingNewTag(true)}
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                style={{ marginTop: theme.spacing(1) }}
              >
                Add New
              </Button>
            </div>
          )}
        </CardContent>

        <Divider />

        <CardActions className={classes.actions}>
          <Button
            color="inherit"
            size="small"
            title="Cancel"
            variant="contained"
            onClick={() => {
              onClose();
            }}
          >
            Cancel
          </Button>

          <Button
            color="primary"
            size="small"
            title="Save"
            variant="contained"
            onClick={() => {
              handleSave();
            }}
          >
            Save
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
};
